import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Parseia uma data do Supabase (que vem com +00:00) como horário local.
 * O Supabase salva timestamps como UTC, mas queremos interpretar como horário local brasileiro.
 * 
 * @param {string} dateString - Data no formato ISO do Supabase (ex: "2026-04-07T09:00:00+00:00")
 * @returns {Date|null} - Objeto Date interpretado como horário local
 */
export const parseLocalDateTime = (dateString) => {
  if (!dateString) return null;
  // Remove o timezone (+00:00 ou Z) e parseia como local
  const withoutTz = dateString.replace(/\+00:00$/, '').replace(/Z$/, '');
  return new Date(withoutTz);
};

/**
 * Formata uma data/hora do Supabase para exibição
 * 
 * @param {string} dateString - Data no formato ISO do Supabase
 * @param {string} formatStr - String de formato do date-fns (padrão: "dd/MM 'às' HH:mm")
 * @returns {string} - Data formatada
 */
export const formatDateTime = (dateString, formatStr = "dd/MM 'às' HH:mm") => {
  const date = parseLocalDateTime(dateString);
  if (!date || isNaN(date.getTime())) return '';
  return format(date, formatStr, { locale: ptBR });
};

/**
 * Formata apenas o horário de uma data do Supabase
 * 
 * @param {string} dateString - Data no formato ISO do Supabase
 * @returns {string} - Horário formatado (ex: "09:00")
 */
export const formatTime = (dateString) => {
  return formatDateTime(dateString, 'HH:mm');
};

/**
 * Formata apenas a data de uma data do Supabase
 * 
 * @param {string} dateString - Data no formato ISO do Supabase
 * @param {string} formatStr - String de formato do date-fns (padrão: "dd/MM/yyyy")
 * @returns {string} - Data formatada
 */
export const formatDate = (dateString, formatStr = 'dd/MM/yyyy') => {
  return formatDateTime(dateString, formatStr);
};

/**
 * Verifica se duas datas são do mesmo dia
 * 
 * @param {string} dateString1 - Primeira data
 * @param {Date} date2 - Segunda data (objeto Date)
 * @returns {boolean}
 */
export const isSameDayLocal = (dateString1, date2) => {
  const date1 = parseLocalDateTime(dateString1);
  if (!date1 || !date2) return false;
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};
